import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, Box, TextField, List, ListItemButton, ListItemText, ListItemIcon, Typography } from '@mui/material';
import { Campaign, ContentCopy, TrendingUp, People } from '@mui/icons-material';

export interface CommandItem {
  id: string;
  label: string;
  hint?: string;
  icon?: React.ReactNode;
  onSelect: () => void;
}

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  items: CommandItem[];
}

const CommandPalette: React.FC<CommandPaletteProps> = ({ open, onClose, items }) => {
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    return items.filter(i => i.label.toLowerCase().includes(q) || (i.hint || '').toLowerCase().includes(q));
  }, [items, query]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex(prev => Math.min(prev + 1, Math.max(filtered.length - 1, 0)));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex(prev => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        filtered[activeIndex]?.onSelect();
        onClose();
      } else if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, filtered, activeIndex, onClose]);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setActiveIndex(0);
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 2, borderBottom: '1px solid var(--line)' }}>
          <TextField
            autoFocus
            fullWidth
            placeholder="Befehl suchen…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            InputProps={{
              sx: {
                height: 44,
                borderRadius: '999px',
                backgroundColor: 'var(--slate-50)'
              }
            }}
          />
          <Typography variant="caption" sx={{ color: 'var(--muted)', mt: 1, display: 'block' }}>
            Tippe, um zu filtern • Enter zum Ausführen • Esc schließen
          </Typography>
        </Box>
        <List sx={{ maxHeight: 320, overflow: 'auto', py: 0 }}>
          {filtered.map((item, idx) => (
            <ListItemButton
              key={item.id}
              selected={idx === activeIndex}
              onClick={() => { item.onSelect(); onClose(); }}
              sx={{ borderRadius: 2, mx: 1, my: 0.5 }}
            >
              {item.icon && (
                <ListItemIcon sx={{ minWidth: 36 }}>
                  {item.icon}
                </ListItemIcon>
              )}
              <ListItemText
                primary={item.label}
                secondary={item.hint}
                primaryTypographyProps={{ fontWeight: 600 }}
                secondaryTypographyProps={{ color: 'var(--muted)' }}
              />
            </ListItemButton>
          ))}
          {filtered.length === 0 && (
            <Box sx={{ p: 3, textAlign: 'center', color: 'var(--muted)' }}>
              Keine Ergebnisse
            </Box>
          )}
        </List>
      </DialogContent>
    </Dialog>
  );
};

export default CommandPalette;

